import React, { useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ChevronDown, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MultiSelect } from "./ui/multi-select";
import StatusMultiSelect from "./StatusMultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

interface FilterOption {
  label: string;
  key: string;
  isMulti?: boolean;
  type?: "select" | "monthYear";
  placeholder?: string;
  values?: Array<{
    label: string;
    key: string;
  }>;
}

interface FilterDropdownProps {
  filtersOptions: FilterOption[];
  filters: Record<string, any>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  defaultFilters?: Record<string, any>;
  /** Called after filter state is cleared (e.g. reset date range in parent). */
  onClearAllFilters?: () => void;
  isHideClearAllFilters?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filtersOptions,
  filters,
  setFilters,
  defaultFilters = {},
  onClearAllFilters,
  isHideClearAllFilters = false,
}) => {
  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    filtersOptions.forEach((opt) => {
      const value = filters[opt.key];
      if (Array.isArray(value) && value.length > 0) {
        count += value.length;
      } else if (value && value !== "") {
        count += 1;
      }
    });
    return count;
  }, [filters, filtersOptions]);

  // Check if any filter is active
  const hasActiveFilters = activeFiltersCount > 0;

  // Clear all filters
  const clearAllFilters = () => {
    const clearedFilters: Record<string, any> = {};
    filtersOptions.forEach((opt) => {
      const filterType = opt.type || "select";
      // If there's a default value for this filter, use it; otherwise clear it
      if (defaultFilters && defaultFilters[opt.key] !== undefined) {
        clearedFilters[opt.key] = defaultFilters[opt.key];
      } else if (filterType === "monthYear") {
        clearedFilters[opt.key] = "";
      } else {
        clearedFilters[opt.key] = opt.isMulti ? [] : "";
      }
    });
    setFilters(clearedFilters);
    onClearAllFilters?.();
  };

  // Get active filter count for a specific option
  const getOptionActiveCount = (opt: FilterOption) => {
    const value = filters[opt.key];
    if (Array.isArray(value)) {
      return value.length;
    }
    return value && value !== "" ? 1 : 0;
  };

  // Generate month options; do not offer future months for the current year.
  const getMonthOptions = (selectedYear?: string) => {
    const now = new Date();
    const maxMonth =
      selectedYear && selectedYear === String(now.getFullYear())
        ? now.getMonth() + 1
        : 12;

    return Array.from({ length: maxMonth }, (_, i) => {
      const date = new Date(2000, i, 1);
      return {
        label: date.toLocaleString("default", { month: "long" }),
        value: String(i + 1).padStart(2, "0"),
      };
    });
  };

  /** When month is picked before year, avoid future months in the current year. */
  const resolveYearForMonthSelection = (month: string, existingYear?: string) => {
    if (existingYear) return existingYear;

    const now = new Date();
    const currentYear = now.getFullYear();
    const monthNum = Number(month);
    if (!monthNum) return String(currentYear);

    // e.g. pick December in July -> use previous year (12-2025 not 12-2026)
    if (monthNum > now.getMonth() + 1) {
      return String(currentYear - 1);
    }

    return String(currentYear);
  };

  // Generate year options (current year and 25 years back only).
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 75; i <= currentYear; i++) {
      years.push({ label: String(i), value: String(i) });
    }
    return years.reverse();
  };

  // Format month/year value for display
  const formatMonthYear = (value: string) => {
    if (!value) return "";
    const [month, year] = value.split("-");
    if (!month || !year) return "";
    const monthName = new Date(2000, parseInt(month) - 1, 1).toLocaleString("default", { month: "short" });
    return `${monthName} ${year}`;
  };

  return (
    <div className="flex items-center w-auto max-w-full shrink-0 overflow-x-auto justify-start md:justify-end gap-2">
      {filtersOptions.map((opt) => {
        const activeCount = getOptionActiveCount(opt);
        const isActive = activeCount > 0;
        const filterType = opt.type || "select";
        const selectedMonthYear = filterType === "monthYear" ? filters[opt.key] || "" : "";
        const selectedYear = selectedMonthYear.split("-")[1] || "";

        return (
          <div key={opt.key}>
            {filterType === "monthYear" ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-9 rounded-lg font-medium text-sm px-3 gap-2 ${
                      isActive
                        ? "bg-primary-dark text-white hover:bg-primary "
                        : "bg-white text-gray-700 hover:text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={
                        filters[opt.key] ? undefined : "text-muted-foreground"
                      }
                    >
                      {filters[opt.key]
                        ? formatMonthYear(filters[opt.key])
                        : opt.placeholder || "MM-YYYY"}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  <div className="flex flex-col gap-4">
                    <div className="flex gap-2">
                      <Select
                        value={
                          filters[opt.key]
                            ? filters[opt.key].split("-")[0] || ""
                            : ""
                        }
                        onValueChange={(month) => {
                          const currentValue = filters[opt.key] || "";
                          const existingYear = currentValue.split("-")[1] || "";
                          const currentYear = resolveYearForMonthSelection(month, existingYear);
                          setFilters((p: any) => ({
                            ...p,
                            [opt.key]: month && currentYear ? `${month}-${currentYear}` : "",
                          }));
                        }}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {getMonthOptions(selectedYear || undefined).map((month) => (
                            <SelectItem className="capitalize" key={month.value} value={month.value}>
                              {month.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={
                          filters[opt.key]
                            ? filters[opt.key].split("-")[1] || ""
                            : ""
                        }
                        onValueChange={(year) => {
                          const currentValue = filters[opt.key] || "";
                          const parts = currentValue.split("-");
                          const now = new Date();
                          const maxMonth = year === String(now.getFullYear()) ? now.getMonth() + 1 : 12;
                          const selectedMonth = Number(parts[0]) || now.getMonth() + 1;
                          const currentMonth = String(Math.min(selectedMonth, maxMonth)).padStart(2, "0");
                          setFilters((p: any) => ({
                            ...p,
                            [opt.key]: year && currentMonth ? `${currentMonth}-${year}` : (year ? `01-${year}` : ""),
                          }));
                        }}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {getYearOptions().map((year) => (
                            <SelectItem className="capitalize" key={year.value} value={year.value}>
                              {year.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {filters[opt.key] && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setFilters((p: any) => ({ ...p, [opt.key]: "" }))
                        }
                        className="w-full"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            ) : opt.isMulti ? (
              // opt.key === "status" || opt.key === "caretakers" ? (
                <StatusMultiSelect
                  options={opt.values || []}
                  value={
                    Array.isArray(filters[opt.key])
                      ? filters[opt.key]
                      : filters[opt.key]
                        ? [filters[opt.key]]
                        : []
                  }
                  onChange={(val) => {
                    setFilters((p: any) => ({ ...p, [opt.key]: val }));
                  }}
                  placeholder={opt.label}
                  listMaxHeightClassName={
                    opt.key === "facilityIds" ? "max-h-96" : "max-h-60"
                  }
                />
              // ) : (
              //   <MultiSelect
              //     className="w-auto"
              //     onValueChange={(val) => {
              //       setFilters((p: any) => ({ ...p, [opt.key]: val }));
              //     }}
              //     options={opt.values?.map((v: any) => ({
              //       label: v.label,
              //       value: v.key,
              //     })) || []}
              //     value={
              //       Array.isArray(filters[opt.key])
              //         ? filters[opt.key]
              //         : [filters[opt.key]]
              //     }
              //     defaultValue={
              //       Array.isArray(filters[opt.key])
              //         ? filters[opt.key]
              //         : [filters[opt.key]]
              //     }
              //     placeholder={opt.label}
              //     popoverClassName={`h-9 rounded-lg font-medium text-sm ${
              //       isActive
              //         ? "bg-primary-dark text-white hover:bg-primary-dark "
              //         : "bg-white text-gray-700 border-gray-300 hover:bg-gray-800"
              //     }`}
                  
                 
              //   />
              // )
            ) : (
              <Select
                value={filters[opt.key] || ""}
                onValueChange={(value) =>
                  setFilters((p: any) => ({ ...p, [opt.key]: value }))
                }
              >
                <SelectTrigger
                  className={`h-9 rounded-lg font-medium text-sm px-3 gap-2  ${
                    isActive
                      ? "bg-primary-dark text-white hover:bg-primary [&>svg]:text-white"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 [&>svg]:text-[#DDDDDD]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{opt.label}</span>
                    {/* {isActive && activeCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="rounded-full bg-white/20 text-white px-1.5 py-0.5 text-xs font-semibold"
                      >
                        {activeCount}
                      </Badge>
                    )} */}
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {opt.values?.map((v: any) => (
                    <SelectItem className="capitalize" key={v.key} value={v.key}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        );
      })}

      {hasActiveFilters && !isHideClearAllFilters && (
        <Button
          variant="secondary"
          size="sm"
          onClick={clearAllFilters}
          className="max-h-8 text-white text-sm "
        >
          Clear all
        </Button>
      )}
    </div>
  );
};

export default FilterDropdown;